import React, { useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import Typed from "typed.js";

export const HeroComponent = () => {
  const typedElement = useRef(null);

  useEffect(() => {
    const typed = new Typed(typedElement.current, {
      strings: [
        "Software Engineer",
        "Full Stack Developer",
        "UI/UX Designer",
        "Web Application Specialist",
      ],
      typeSpeed: 50,
      backSpeed: 30,
      loop: true,
      smartBackspace: true,
      showCursor: true,
      cursorChar: "|",
    });

    return () => {
      typed.destroy();
    };
  }, []);

  return (
    <section className="home-hero-section bg-dark text-white position-relative overflow-hidden">
      {/* Animated background elements */}
      <div className="position-absolute top-0 start-0 w-100 h-100">
        <div className="position-absolute top-0 start-0 w-100 h-100 bg-primary opacity-10"></div>
        <div className="position-absolute bottom-0 end-0 circle-shape bg-primary opacity-05 rounded-circle"></div>
      </div>

      <div className="container py-5">
        <div className="row align-items-center min-vh-80 py-5">
          {/* Text Content */}
          <div className="col-lg-6 pe-lg-5 mb-5 mb-lg-0 position-relative">
            <div className="mb-4">
              <span className="badge bg-primary bg-opacity-20 text-white fw-normal px-3 py-2 mb-3 d-inline-block">
                <i className="fa fa-code me-2"></i> Transforming Ideas Into
                Reality
              </span>
              <h1 className="display-3 fw-bold mb-3">
                Hi, I'm a{" "}
                <span className="text-primary">
                  <span ref={typedElement}></span>
                </span>
              </h1>
              <p className="lead text-muted mb-4">
                I create elegant, functional digital experiences with modern web
                technologies and user-centric design principles. Let's build
                something amazing together.
              </p>
              <div className="d-flex flex-wrap gap-3">
                <Link
                  to="/api/courses"
                  className="btn btn-primary btn-lg px-4 py-3 rounded-pill fw-bold"
                >
                  <i className="fa fa-book me-2"></i> Explore Courses
                </Link>
                <Link
                  to="/api/get-in-touch/contact"
                  className="btn btn-outline-light btn-lg px-4 py-3 rounded-pill fw-bold"
                >
                  <i className="fa fa-paper-plane me-2"></i> Contact Me
                </Link>
              </div>
            </div>

            {/* Stats */}
            <div className="d-flex flex-wrap gap-4 mt-5 pt-3">
              <div className="text-center">
                <h3 className="display-5 fw-bold text-primary mb-1">6+</h3>
                <p className="text-muted mb-0">Years Experience</p>
              </div>
              <div className="text-center">
                <h3 className="display-5 fw-bold text-primary mb-1">50+</h3>
                <p className="text-muted mb-0">Projects Completed</p>
              </div>
              <div className="text-center">
                <h3 className="display-5 fw-bold text-primary mb-1">100%</h3>
                <p className="text-muted mb-0">Client Satisfaction</p>
              </div>
            </div>
          </div>

          {/* Graphic Content */}
          <div className="col-lg-6 position-relative">
            <div className="hero-graphic position-relative">
              {/* Main profile illustration */}
              <div className="position-relative z-index-1">
                <div className="profile-circle bg-primary bg-opacity-10 rounded-circle position-relative overflow-hidden">
                  <div className="position-absolute top-50 start-50 translate-middle">
                    <div
                      className="position-relative"
                      style={{ width: "300px", height: "300px" }}
                    >
                      {/* Animated SVG */}
                      <svg
                        width="100%"
                        height="100%"
                        viewBox="0 0 500 500"
                        className="position-absolute top-0 start-0"
                      >
                        <circle
                          cx="250"
                          cy="250"
                          r="200"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeOpacity="0.3"
                        >
                          <animate
                            attributeName="r"
                            from="200"
                            to="210"
                            dur="4s"
                            repeatCount="indefinite"
                            values="200; 210; 200"
                            keyTimes="0; 0.5; 1"
                          />
                        </circle>
                        <path
                          d="M150 200 L200 250 L150 300"
                          stroke="currentColor"
                          strokeWidth="4"
                          fill="none"
                          strokeLinecap="round"
                        >
                          <animate
                            attributeName="d"
                            dur="3s"
                            repeatCount="indefinite"
                            values="M150 200 L200 250 L150 300;
                                    M140 200 L190 250 L140 300;
                                    M150 200 L200 250 L150 300"
                          />
                        </path>
                        <path
                          d="M350 200 L300 250 L350 300"
                          stroke="currentColor"
                          strokeWidth="4"
                          fill="none"
                          strokeLinecap="round"
                        >
                          <animate
                            attributeName="d"
                            dur="3s"
                            repeatCount="indefinite"
                            values="M350 200 L300 250 L350 300;
                                    M360 200 L310 250 L360 300;
                                    M350 200 L300 250 L350 300"
                          />
                        </path>
                      </svg>

                      {/* Central avatar placeholder */}
                      <div
                        className="position-absolute top-50 start-50 translate-middle rounded-circle overflow-hidden"
                        style={{
                          width: "180px",
                          height: "180px",
                          background: "rgba(255,255,255,0.1)",
                        }}
                      >
                        <i className="fa fa-user fa-5x text-white position-absolute top-50 start-50 translate-middle"></i>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Floating tech icons */}
              <div className="tech-icon tech-icon-1">
                <i className="fa fa-react fa-2x text-info"></i>ReactJS
              </div>
              <div className="tech-icon tech-icon-2">
                <i className="fa fa-js fa-2x text-warning"></i>JS
              </div>
              <div className="tech-icon tech-icon-3">
                <i className="fa fa-html5xx fa-2x text-danger"></i>ExpressJS
              </div>
              <div className="tech-icon tech-icon-4">
                <i className="fa fa-css3xx fa-2x text-primary"></i>MongoDB
              </div>
              <div className="tech-icon tech-icon-5">
                <i className="fab fa-node-js fa-2x text-success"></i>NodeJS
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Scroll indicator */}
      <div className="scroll-indicator position-absolute start-50 translate-middle-x bottom-0 mb-4">
        <a
          href="#subscribe"
          className="text-white text-decoration-none d-flex flex-column align-items-center"
        >
          <span className="mb-2 small">Scroll Down</span>
          <i className="fa fa-chevron-down fa-lg animate-bounce"></i>
        </a>
      </div>
    </section>
  );
};
